import React from "react";
import { Link } from "react-router-dom";
import { Eye, Pencil, FileDown } from "lucide-react";
import { DD_MM_YYYY } from "src/helpers";
import { formatFloatWithTwoDecimals } from "src/utils";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { PopulatedPalletRecord, TableColumn } from "src/types";
import { PalletColumnHeader } from "src/enums";
import { Table } from "src/components";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/shadcn/ui/tooltip";
import { PalletService } from "src/services/pallets";
import { usePalletFileDownload } from "src/hooks";
import { PalletStatusPill } from "src/components/relabelling/PalletStatusPill";
import { AnimalBadge } from "src/components/tables/dashboardTables/relabelCasesTrackingTable/AnimalBadge";
import { AnimalNonCertifiedBadge } from "src/components/tables/dashboardTables/relabelCasesTrackingTable/AnimalNonCertifiedBadge";

interface PalletsDashTableProps {
  pallets: PopulatedPalletRecord[];
  palletService: PalletService;
}

const PalletsDashTable: React.FC<PalletsDashTableProps> = ({ pallets, palletService }) => {
  const { downloadFile, isFileDownloading } = usePalletFileDownload(palletService);

  const palletTableColumns: TableColumn<PalletColumnHeader, PopulatedPalletRecord>[] = [
    {
      header: PalletColumnHeader.PALLET_ID,
      column: (row: PopulatedPalletRecord) => (
        <Link
          to={`${ROUTES_CONFIG.viewPallet.path}?id=${row.id}`}
          className="font-mono text-sm text-primary-900 hover:text-primary-700 hover:underline transition-colors"
        >
          {row.id}
        </Link>
      ),
      sticky: true,
      styles: { minWidth: 150 }
    },
    {
      header: PalletColumnHeader.STATUS,
      column: (row: PopulatedPalletRecord) => (
        <span className="flex justify-center">
          <PalletStatusPill status={row.status} />
        </span>
      ),
      styles: { textAlign: "center", minWidth: 140 }
    },
    {
      header: PalletColumnHeader.LOCATION,
      column: (row: PopulatedPalletRecord) => (
        <span className="text-sm text-secondary-700">{row.locationDisplayName ?? "—"}</span>
      ),
      styles: { textAlign: "center", minWidth: 130 }
    },
    {
      header: PalletColumnHeader.PO_IDS,
      column: (row: PopulatedPalletRecord) => {
        const sourceIds = Array.from(new Set([...row.purchaseOrderIds, ...(row.transferOrderIds ?? [])]));
        return (
          <div className="flex flex-wrap gap-1 max-w-[200px]">
            {sourceIds.length === 0 && <span className="text-xs text-secondary-400">—</span>}
            {sourceIds.map((id) => (
              <span
                key={id}
                className="inline-block px-2 py-0.5 text-xs font-medium bg-secondary-100 text-secondary-700 rounded"
              >
                {id}
              </span>
            ))}
          </div>
        );
      }
    },
    {
      header: PalletColumnHeader.ANIMAL,
      column: (row: PopulatedPalletRecord) => {
        if (row.isAnimalNonCertified) return <AnimalNonCertifiedBadge />;
        if (row.isAnimalSku) return <AnimalBadge />;
        return (
          <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-secondary-100 text-secondary-700 border border-secondary-200">
            Non-Animal
          </span>
        );
      },
      styles: { textAlign: "center", minWidth: 140 }
    },
    {
      header: PalletColumnHeader.TOTAL_CASES,
      column: (row: PopulatedPalletRecord) => String(row.totalCases),
      styles: { textAlign: "center" }
    },
    {
      header: PalletColumnHeader.TOTAL_UNITS,
      column: (row: PopulatedPalletRecord) => String(row.totalUnits),
      styles: { textAlign: "center" }
    },
    {
      header: PalletColumnHeader.TOTAL_VALUE,
      column: (row: PopulatedPalletRecord) => (row.totalValue ? `£${formatFloatWithTwoDecimals(row.totalValue)}` : "—"),
      styles: { textAlign: "center" }
    },
    {
      header: PalletColumnHeader.CREATION_DATE,
      column: (row: PopulatedPalletRecord) =>
        row.createdAt ? DD_MM_YYYY.formatDate(new Date(row.createdAt)) : undefined,
      styles: { textAlign: "center" }
    },
    {
      header: PalletColumnHeader.ACTIONS,
      column: (row: PopulatedPalletRecord) => {
        const canEdit = row.status === "COMPLETE";
        return (
          <TooltipProvider>
            <div className="flex items-center justify-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={`${ROUTES_CONFIG.viewPallet.path}?id=${row.id}`}
                    className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                  >
                    <Eye className="h-4 w-4 text-muted-600" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View Pallet</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => downloadFile(row.id)}
                    disabled={isFileDownloading}
                    className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FileDown className="h-4 w-4 text-muted-600" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Download Pallet Label</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  {canEdit ? (
                    <Link
                      to={`${ROUTES_CONFIG.editPallet.path}?id=${row.id}`}
                      className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                    >
                      <Pencil className="h-4 w-4 text-muted-600" />
                    </Link>
                  ) : (
                    <span className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md cursor-not-allowed opacity-40">
                      <Pencil className="h-4 w-4 text-muted-600" />
                    </span>
                  )}
                </TooltipTrigger>
                <TooltipContent>
                  <p>{canEdit ? "Edit Pallet" : `Cannot edit — pallet is ${row.status}`}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        );
      },
      styles: { textAlign: "center" }
    }
  ];

  return <Table<PalletColumnHeader, PopulatedPalletRecord> data={pallets} columns={palletTableColumns} />;
};

export { PalletsDashTable };
